import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators'
import { of } from 'rxjs'
import { ActionFailed, Toaster } from '@tokeet-frontend/tv3-platform'
import { VendorsService } from './service'
import {
  AddVendor,
  AddVendorComplete,
  LoadVendors,
  LoadVendersComplete,
  UpdateVendor,
  UpdateVendorComplete,
  GetVendorById,
  GetVendorByIdComplete,
  DeleteVendor,
  DeleteVendorComplete,
  UpdateVendors,
  UpdateVendorsComplete,
  DeleteVendors,
  DeleteVendorsComplete,
} from './actions'

@Injectable()
export class VendorsEffects {
  @Effect()
  load$ = this.actions$.pipe(
    ofType(LoadVendors),
    switchMap(() =>
      this.vendorService.getAll().pipe(
        map((items) => LoadVendersComplete({ items })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  get$ = this.actions$.pipe(
    ofType(GetVendorById),
    switchMap(({ id }) =>
      this.vendorService.get(id).pipe(
        map((item) => GetVendorByIdComplete({ item })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  add$ = this.actions$.pipe(
    ofType(AddVendor),
    switchMap(({ payload }) =>
      this.vendorService.add(payload).pipe(
        map((item) => AddVendorComplete({ item })),
        tap(() => this.toaster.success($localize`Vendor added successfully`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  update$ = this.actions$.pipe(
    ofType(UpdateVendor),
    switchMap(({ id, payload, silent }) =>
      this.vendorService.update(id, payload).pipe(
        map((item) => UpdateVendorComplete({ item })),
        tap(() => !silent && this.toaster.success($localize`Vendor updated successfully`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateVendors$ = this.actions$.pipe(
    ofType(UpdateVendors),
    switchMap(({ items }) =>
      this.vendorService.updateVendors(items).pipe(
        tap(() => this.toaster.success($localize`Vendors updated successfully`)),
        map((items) => UpdateVendorsComplete({ updates: items.map((item) => ({ id: item.pkey, changes: item })) })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  delete$ = this.actions$.pipe(
    ofType(DeleteVendor),
    switchMap(({ id, silent }) =>
      this.vendorService.delete(id).pipe(
        map(() => DeleteVendorComplete({ id })),
        tap(() => !silent && this.toaster.success($localize`Vendor deleted successfully`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  deleteVendors$ = this.actions$.pipe(
    ofType(DeleteVendors),
    switchMap(({ ids }) =>
      this.vendorService.deleteVendors(ids).pipe(
        map(() => DeleteVendorsComplete({ ids })),
        tap(() => this.toaster.success($localize`Vendors deleted successfully`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  constructor(private actions$: Actions, private toaster: Toaster, private vendorService: VendorsService) {}
}
